import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_IMAGE,
  DEFAULT_KEYWORDS,
  SITE_NAME,
  SITE_URL,
  seoForPath,
} from "../site/seo";

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Updates document title + meta tags on every route change (SPA SEO).
 */
export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = seoForPath(pathname);
    const safePath = pathname.startsWith("/change-password")
      ? "/change-password"
      : pathname === "/"
        ? ""
        : pathname.replace(/\/$/, "");
    const canonical = `${SITE_URL}${safePath || ""}`;
    const title = page.title;
    const description = page.description;
    const image = page.image || DEFAULT_IMAGE;

    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", page.keywords || DEFAULT_KEYWORDS);
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta(
      "name",
      "robots",
      page.noindex ? "noindex, follow" : "index, follow, max-image-preview:large"
    );
    upsertMeta("name", "theme-color", "#040A20");

    upsertMeta("property", "og:type", pathname === "/" ? "website" : "article");
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "nl_NL");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:image:alt", `${SITE_NAME} — Het is zo simpel.`);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    upsertLink("canonical", canonical);

    upsertJsonLd("seo-org", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo512.png`,
      email: "suriweb@suriwebwinkel.nl",
      telephone: "+31-6-3706-0001",
      sameAs: [
        "https://apps.apple.com/app/suriwebwinkel/id1573691847",
        "https://play.google.com/store/apps/details?id=nl.suriwebwinkel.suriwebdev",
      ],
      slogan: "Het is zo simpel.",
      areaServed: [
        { "@type": "Country", name: "Suriname" },
        { "@type": "Continent", name: "Europe" },
      ],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+31-6-3706-0001",
        contactType: "customer service",
        availableLanguage: ["Dutch", "English"],
        areaServed: ["NL", "BE", "DE", "FR", "GB", "SR"],
      },
    });

    upsertJsonLd("seo-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      description: description,
      inLanguage: "nl-NL",
      publisher: { "@type": "Organization", name: SITE_NAME },
    });

    upsertJsonLd("seo-app", {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      name: SITE_NAME,
      operatingSystem: "iOS, Android",
      applicationCategory: "ShoppingApplication",
      description:
        "Bestel boodschappen en cadeaus vanuit Europa voor bezorging in Suriname. Het is zo simpel.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
      },
      downloadUrl: [
        "https://apps.apple.com/app/suriwebwinkel/id1573691847",
        "https://play.google.com/store/apps/details?id=nl.suriwebwinkel.suriwebdev",
      ],
    });
  }, [pathname]);

  return null;
}
